/**
 * The rave's weather on this phone (plan.md step 65, §65.2) — one store per
 * edition, shared by every screen that shows it (the menu row, the Weather
 * page, Live, the countdown), so the forecast is asked for once.
 *
 * - **Stored first, network second.** The copy kept on the phone
 *   (`myrave:{editionId}:weather`, with its ETag) or the bundle's `weather`
 *   block — whichever was fetched later — is shown AT ONCE. `GET
 *   /editions/:id/weather` is asked behind it (If-None-Match), when the page
 *   opens and every 15 minutes while it is open, and a new answer is kept.
 *   Nothing waits for it; a request that hangs is cut at `WEATHER_TIMEOUT_MS`.
 * - **No answer is "offline", and the screens say how old the copy is**
 *   (`forecastAge`) instead of pretending to be live.
 * - **A 404 is an answer**: the module is off or the rave has no location —
 *   the stored copy is dropped and there is no weather anywhere.
 * - **Module `weather` off → nothing**: no request, no stored copy shown.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { dayBucket, sunTimes, wallClock, type RaveWeather, type WeatherCondition, type WeatherHour, weatherIcon } from '@myrave/shared';
import { authHeader } from '@/lib/api';
import { apiUrl } from '@/lib/tenant';
import { noteServerReached } from '@/lib/reach';
import { inPreview } from '@/lib/preview';
import { readJson, remove, writeJson } from '@/lib/storage';
import { signedInHere } from '@/lib/signOut';

/** The device key: `myrave:{editionId}:weather` (hard rule 6). */
export const WEATHER_KEY = 'weather';
/** A copy is asked for again after this long while a screen shows it. */
export const WEATHER_STALE_MS = 15 * 60_000;
/** A request with no answer by then counts as offline. */
export const WEATHER_TIMEOUT_MS = 10_000;

/** Where the copy on screen came from: the server just now, or what the phone had. */
export type WeatherSource = 'fresh' | 'stored' | 'offline';

export interface RaveWeatherState {
  readonly weather: RaveWeather | null;
  /** `fresh` — the server answered in this visit; `stored` — not asked yet; `offline` — asked, no answer. */
  readonly source: WeatherSource;
}

interface Kept { readonly etag: string | null; readonly weather: RaveWeather }

interface Entry {
  state: RaveWeatherState;
  etag: string | null;
  askedAt: number;
  inFlight: Promise<void> | null;
  readonly listeners: Set<() => void>;
}

const entries = new Map<string, Entry>();
const OFF: RaveWeatherState = { weather: null, source: 'stored' };

/** Test support. */
export function resetWeatherForTests(): void {
  entries.clear();
}

function isWeather(v: unknown): v is RaveWeather {
  return Boolean(v) && typeof v === 'object' && Array.isArray((v as RaveWeather).days) && Array.isArray((v as RaveWeather).hours);
}

const fetchedMs = (w: RaveWeather | null | undefined) => (w?.fetchedAt ? Date.parse(w.fetchedAt) : -1);

function entryFor(editionId: string, fromBundle: RaveWeather | null | undefined): Entry {
  let e = entries.get(editionId);
  if (e) return e;
  // Signed out is online only (the user, 2026-10-27): nothing kept is read, nothing is kept.
  const kept = signedInHere() ? readJson<Kept | null>(editionId, WEATHER_KEY, null) : null;
  const stored = kept && isWeather(kept.weather) ? kept : null;
  const bundled = isWeather(fromBundle) ? fromBundle : null;
  // Whichever was fetched later — the bundle is rebuilt rarely, the phone's copy may be older still.
  const useBundle = bundled && (!stored || fetchedMs(bundled) > fetchedMs(stored.weather));
  e = {
    state: { weather: useBundle ? bundled : stored?.weather ?? null, source: 'stored' },
    etag: useBundle ? null : stored?.etag ?? null,
    askedAt: 0,
    inFlight: null,
    listeners: new Set(),
  };
  entries.set(editionId, e);
  return e;
}

function publish(e: Entry, state: RaveWeatherState): void {
  e.state = state;
  for (const l of e.listeners) l();
}

/** Ask the server once (deduplicated); never throws. */
function ask(editionId: string, e: Entry, force = false): Promise<void> {
  if (e.inFlight) return e.inFlight;
  if (!force && Date.now() - e.askedAt < WEATHER_STALE_MS) return Promise.resolve();
  // A public page's preview is signed out by design and reads the bundle alone.
  if (inPreview()) return Promise.resolve();
  e.askedAt = Date.now();
  const ctrl = typeof AbortController === 'undefined' ? null : new AbortController();
  const timer = ctrl ? setTimeout(() => ctrl.abort(), WEATHER_TIMEOUT_MS) : null;
  e.inFlight = (async () => {
    try {
      const res = await fetch(`${apiUrl()}/editions/${editionId}/weather`, {
        credentials: 'include',
        headers: { ...authHeader(), ...(e.etag ? { 'if-none-match': e.etag } : {}) },
        signal: ctrl?.signal,
      });
      noteServerReached();
      if (res.status === 304) { publish(e, { ...e.state, source: 'fresh' }); return; }
      if (res.status === 404) {
        // The module is off, or the rave has no location: no weather anywhere.
        remove(editionId, WEATHER_KEY);
        e.etag = null;
        publish(e, { weather: null, source: 'fresh' });
        return;
      }
      if (!res.ok) { publish(e, { ...e.state, source: 'offline' }); return; }
      const body: unknown = await res.json();
      if (!isWeather(body)) { publish(e, { ...e.state, source: 'offline' }); return; }
      e.etag = res.headers?.get?.('etag') ?? null;
      if (signedInHere()) writeJson(editionId, WEATHER_KEY, { etag: e.etag, weather: body } satisfies Kept);
      publish(e, { weather: body, source: 'fresh' });
    } catch {
      // No signal, a hang, a refusal we cannot read: the copy stays, and says its age.
      e.askedAt = 0;
      publish(e, { ...e.state, source: 'offline' });
    } finally {
      if (timer) clearTimeout(timer);
      e.inFlight = null;
    }
  })();
  return e.inFlight;
}

/**
 * The rave's weather: the stored copy at once, the server's answer when it
 * comes. `null` weather — the module is off, the rave has no location, or
 * nothing has been fetched yet — means no weather anywhere.
 */
export function useRaveWeather(
  editionId: string | undefined,
  bundle: { readonly modules?: readonly string[]; readonly weather?: RaveWeather | null } | undefined,
): RaveWeatherState {
  const on = Boolean(editionId) && Boolean(bundle?.modules?.includes('weather'));
  const id = on ? editionId! : '';
  const state = useSyncExternalStore(
    (cb) => {
      if (!on) return () => {};
      const e = entryFor(id, bundle?.weather);
      e.listeners.add(cb);
      return () => { e.listeners.delete(cb); };
    },
    () => (on ? entryFor(id, bundle?.weather).state : OFF),
  );
  useEffect(() => {
    if (!on) return undefined;
    const e = entryFor(id, bundle?.weather);
    void ask(id, e);
    const tick = setInterval(() => void ask(id, e), WEATHER_STALE_MS);
    const online = () => void ask(id, e, true);
    window.addEventListener('online', online);
    return () => { clearInterval(tick); window.removeEventListener('online', online); };
    // The bundle's block is read once per edition, when the store is made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, id]);
  return on ? state : OFF;
}

// ---- what the screens read --------------------------------------------------

/** Rain worth a word: a likely chance, or a real amount. */
export function rainy(h: { rainChance: number | null; rainMm?: number | null; condition: WeatherCondition }): boolean {
  if (h.condition === 'RAIN' || h.condition === 'HEAVY_RAIN' || h.condition === 'THUNDER') return (h.rainChance ?? 100) >= 40;
  return (h.rainChance ?? 0) >= 50 || (h.rainMm ?? 0) >= 1;
}

/** Strong wind, the §65.5 threshold: gusts of 60 km/h or more. */
export const STRONG_GUST_KMH = 60;
export function windy(h: { gustKmh: number | null; windKmh?: number | null }): boolean {
  return (h.gustKmh ?? 0) >= STRONG_GUST_KMH || (h.windKmh ?? 0) >= STRONG_GUST_KMH;
}

/** The forecast hours that overlap `[from, to)`. */
export function hoursDuring(w: RaveWeather | null, from: Date, to: Date): WeatherHour[] {
  if (!w) return [];
  const a = from.getTime();
  const b = to.getTime();
  return w.hours.filter((h) => {
    const s = Date.parse(h.at);
    return s < b && s + 3_600_000 > a;
  });
}

/** What a set plays through: forecast rain, strong wind — for its small marks beside the time. */
export function setWeather(w: RaveWeather | null, from: Date, to: Date): { rain: boolean; wind: boolean } {
  const hours = hoursDuring(w, from, to);
  return { rain: hours.some(rainy), wind: hours.some(windy) };
}

/** The first hour from `now` (within `withinMs`) that matches — "Rain from 16:00". */
export function firstHour(w: RaveWeather | null, now: Date, test: (h: WeatherHour) => boolean, withinMs = 12 * 3_600_000): WeatherHour | null {
  if (!w) return null;
  const n = now.getTime();
  return w.hours.find((h) => {
    const s = Date.parse(h.at);
    return s + 3_600_000 > n && s <= n + withinMs && test(h);
  }) ?? null;
}

/** The hour on now, else null. */
export function hourAt(w: RaveWeather | null, now: Date): WeatherHour | null {
  if (!w) return null;
  const n = now.getTime();
  return w.hours.find((h) => { const s = Date.parse(h.at); return s <= n && n < s + 3_600_000; }) ?? null;
}

/**
 * What "now" is: the provider's current conditions while they are under 90
 * minutes old, else the forecast hour on now (an offline copy from this
 * morning must not show this morning's sky as now), else nothing.
 */
export function currentConditions(w: RaveWeather | null, now: Date): (Omit<WeatherHour, 'rainMm'> & { uv: number | null }) | null {
  if (!w) return null;
  if (w.now && now.getTime() - Date.parse(w.now.at) < 90 * 60_000) return w.now;
  const h = hourAt(w, now);
  return h ? { ...h, uv: null } : null;
}

/**
 * How old the forecast is, in the rave's own clock: `{ when: 'today', time }`,
 * `'yesterday'`, or an earlier `date` — the screen words it. Null when only
 * typical days exist.
 */
export function forecastAge(w: RaveWeather | null, now: Date, timeZone: string):
  { when: 'today' | 'yesterday' | 'earlier'; time: string; day: string } | null {
  if (!w?.fetchedAt) return null;
  const at = new Date(w.fetchedAt);
  if (Number.isNaN(at.getTime())) return null;
  const day = dayBucket(at, timeZone);
  const today = dayBucket(now, timeZone);
  const yesterday = dayBucket(new Date(now.getTime() - 86_400_000), timeZone);
  const when = day === today ? 'today' : day === yesterday ? 'yesterday' : 'earlier';
  return { when, time: wallClock(at, timeZone), day };
}

/** A condition as one emoji — `@myrave/shared`'s table, shared with the always-on card. */
export const conditionIcon = weatherIcon;

type Tr = (english: string, vars?: Record<string, string | number>) => string;

/** The condition in a raver's words — never a meteorologist's. */
export function conditionWord(c: WeatherCondition, tr: Tr, night = false): string {
  switch (c) {
    case 'CLEAR': return night ? tr('Clear sky') : tr('Sunny');
    case 'PARTLY_CLOUDY': return tr('Some clouds');
    case 'CLOUDY': return tr('Cloudy');
    case 'FOG': return tr('Foggy');
    case 'DRIZZLE': return tr('A little rain');
    case 'RAIN': return tr('Rain');
    case 'HEAVY_RAIN': return tr('Heavy rain');
    case 'THUNDER': return tr('Storms');
    case 'SNOW': return tr('Snow');
    case 'WIND': return tr('Windy');
    default: return '';
  }
}

/** Between sunset and sunrise at the rave's place, by its own clock. */
export function isNight(at: Date, lat: number | null | undefined, lng: number | null | undefined, timeZone: string): boolean {
  if (lat == null || lng == null) return false;
  const { sunrise, sunset } = sunTimes(dayBucket(at, timeZone), lat, lng, timeZone);
  if (!sunrise || !sunset) return false;
  return at < sunrise || at >= sunset;
}

/**
 * The rave's weather, read only — no request of its own (a set row is drawn hundreds of times; the page's
 * `useRaveWeather` above, in `FestivalApp`, keeps it fresh).
 */
export function useRaveWeatherRead(
  editionId: string | undefined,
  bundle: { readonly modules?: readonly string[]; readonly weather?: RaveWeather | null } | undefined,
): RaveWeather | null {
  const on = Boolean(editionId) && Boolean(bundle?.modules?.includes('weather'));
  const id = on ? editionId! : '';
  return useSyncExternalStore(
    (cb) => {
      if (!on) return () => {};
      const e = entryFor(id, bundle?.weather);
      e.listeners.add(cb);
      return () => { e.listeners.delete(cb); };
    },
    () => (on ? entryFor(id, bundle?.weather).state.weather : null),
  );
}

/** Worst first: what a set's weather shows when its hours differ (rain matters more than a cloud). */
const SEVERITY: readonly WeatherCondition[] = ['THUNDER', 'HEAVY_RAIN', 'RAIN', 'SNOW', 'DRIZZLE', 'WIND', 'FOG', 'CLOUDY', 'PARTLY_CLOUDY', 'CLEAR'];

/**
 * A set's weather (the user, 2026-11-04: "on each set in the schedule, Live and my lineup — the forecast's average
 * temperature and a small icon"): its hours' average temperature and the worst of their skies; past the hourly
 * forecast (48 h), its day's forecast — the middle of high and low, the day's sky. Null with no forecast for it (a
 * typical day is no forecast).
 */
export function setForecast(w: RaveWeather | null, from: Date, to: Date, timeZone: string): { tempC: number; condition: WeatherCondition } | null {
  if (!w) return null;
  const hours = hoursDuring(w, from, to);
  if (hours.length) {
    const tempC = hours.reduce((s, h) => s + h.tempC, 0) / hours.length;
    const condition = SEVERITY.find((c) => hours.some((h) => h.condition === c)) ?? hours[0]!.condition;
    return { tempC, condition };
  }
  const day = w.days.find((d) => d.source === 'FORECAST' && d.date === dayBucket(from, timeZone));
  if (!day || day.highC == null || day.lowC == null) return null;
  return { tempC: (day.highC + day.lowC) / 2, condition: day.condition };
}
